import React, { useState, useRef } from 'react';
import {
  Clock, Plane, TrendingUp, Upload, AlertTriangle, CheckCircle2, Calendar,
  BarChart3, ArrowUpRight, ArrowDownRight, Minus, Database, ChevronDown, ChevronUp,
  Shield, RefreshCw, FileText
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { projectsService } from '../../services/projects';

interface AodbBenchmarkProps {
  project: any;
  isDark: boolean;
  onProjectUpdate: () => void;
}

// Delta indicator component
const DeltaIndicator = ({ value, unit = 'min', inverted = false }: { value: number | null | undefined; unit?: string; inverted?: boolean }) => {
  if (value === null || value === undefined) return <span className="text-[var(--text-tertiary)]">—</span>;
  const isGood = inverted ? value > 0 : value < 0;
  const isBad = inverted ? value < 0 : value > 0;
  const color = Math.abs(value) < 1 ? 'text-emerald-400' : isGood ? 'text-emerald-400' : isBad ? 'text-red-400' : 'text-[var(--text-secondary)]';
  const Icon = Math.abs(value) < 1 ? Minus : isGood ? ArrowDownRight : ArrowUpRight;
  return (
    <span className={`flex items-center justify-end gap-1 text-xs font-bold ${color}`}>
      <Icon size={12} />
      {value > 0 ? '+' : ''}{value.toFixed(1)} {unit}
    </span>
  );
};

// KPI Card
const KpiCard = ({ label, value, sub, color = 'blue', isDark, badge }: any) => {
  const bgMap: Record<string, string> = {
    blue: isDark ? 'from-blue-500/10 to-blue-600/5 border-blue-500/20' : 'from-blue-50 to-blue-100/50 border-blue-200',
    orange: isDark ? 'from-orange-500/10 to-orange-600/5 border-orange-500/20' : 'from-orange-50 to-orange-100/50 border-orange-200',
    green: isDark ? 'from-emerald-500/10 to-emerald-600/5 border-emerald-500/20' : 'from-emerald-50 to-emerald-100/50 border-emerald-200',
    purple: isDark ? 'from-purple-500/10 to-purple-600/5 border-purple-500/20' : 'from-purple-50 to-purple-100/50 border-purple-200',
    red: isDark ? 'from-red-500/10 to-red-600/5 border-red-500/20' : 'from-red-50 to-red-100/50 border-red-200',
    cyan: isDark ? 'from-cyan-500/10 to-cyan-600/5 border-cyan-500/20' : 'from-cyan-50 to-cyan-100/50 border-cyan-200',
  };
  const valColor: Record<string, string> = {
    blue: 'text-blue-400', orange: 'text-orange-400', green: 'text-emerald-400',
    purple: 'text-purple-400', red: 'text-red-400', cyan: 'text-cyan-400',
  };

  return (
    <div className={`p-4 rounded-xl border bg-gradient-to-br ${bgMap[color] || bgMap.blue} backdrop-blur-sm relative`}>
      {badge && (
        <span className={`absolute top-2 right-2 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-full
          ${badge === 'AODB' ? 'bg-blue-500/20 text-blue-400' : badge === 'GOscore' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-cyan-500/20 text-cyan-400'}`}>
          {badge}
        </span>
      )}
      <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-1">{label}</p>
      <p className={`text-2xl font-black ${valColor[color] || 'text-blue-400'}`}>
        {value !== null && value !== undefined ? value : '—'}
      </p>
      {sub && <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{sub}</p>}
    </div>
  );
};

const AodbBenchmarkSection: React.FC<AodbBenchmarkProps> = ({ project, isDark, onProjectUpdate }) => {
  const { language } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showCarrierTable, setShowCarrierTable] = useState(false);
  const [showTypeTable, setShowTypeTable] = useState(false);
  const [showGoscoreComparison, setShowGoscoreComparison] = useState(false);
  const [showCrossValidation, setShowCrossValidation] = useState(false);

  // Date modal state
  const [showDateModal, setShowDateModal] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)] shadow-sm';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';

  // Extract AODB KPIs (check all possible storage paths)
  const kpis = project?.analysis_results?.kpis?.aodb_operational_kpis
    || project?.analysis_results?.parts?.aodb_operational_kpis
    || project?.analysis_results?.aodb_operational_kpis
    || {};
  const global = kpis?.airport_global || {};
  const precalc = kpis?.precalc_kpis || {};
  const carriers = kpis?.by_carrier || [];
  const types = kpis?.by_aircraft_type || [];
  const quality = kpis?.data_quality || {};
  const crossValidation = kpis?.cross_validation || {};
  const dataSource = kpis?.data_source || 'none';

  // Extract GOscore benchmark
  const benchmark = project?.analysis_results?.goscore_benchmark || null;
  const goscoreData = benchmark?.goscore_data || null;
  const comparison = benchmark?.comparison || null;
  const periodKpis = benchmark?.period_kpis || null;
  const baselineDeviation = benchmark?.baseline_deviation || null;

  const hasAodbData = global && Object.keys(global).length > 0 && global.total_valid_samples > 0;

  // Step 1: User clicks upload → file picker
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setShowDateModal(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Step 2: User confirms dates in modal → actual upload
  const handleConfirmUpload = async () => {
    if (!pendingFile || !project?.id) return;
    
    setShowDateModal(false);
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    
    try {
      const result = await projectsService.uploadGoscoreBenchmark(
        project.id,
        pendingFile,
        dateFrom || undefined,
        dateTo || undefined
      );
      setUploadResult(result);
      onProjectUpdate(); // Refresh project to get stored results
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail || err.message || 'Error uploading CSV');
    } finally {
      setUploading(false);
      setPendingFile(null);
    }
  };

  // Source badge label
  const sourceLabel = dataSource === 'both'
    ? 'Times_Calculation + Timestamps'
    : dataSource === 'precalc'
      ? 'Times_Calculation'
      : dataSource === 'calculated'
        ? 'Timestamps (ALDT/AIBT/AOBT/ATOT)'
        : 'N/A';

  // No early return — GOscore upload should always be visible



  return (
    <div className="space-y-4">
      {/* AODB KPIs SECTION — conditional based on data availability */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        {!hasAodbData ? (
          /* No AODB data yet — show info banner */
          <>
            <h3 className={`font-bold text-lg mb-3 ${textMain} flex items-center gap-2`}>
              <Database size={20} className="text-cyan-400" />
              {language === 'es' ? 'Benchmarking Operacional (AODB)' : 'Operational Benchmarking (AODB)'}
            </h3>
            <div className={`p-4 rounded-lg ${isDark ? 'bg-amber-900/20' : 'bg-amber-50'} flex items-center gap-3`}>
              <AlertTriangle size={20} className="text-amber-400 shrink-0" />
              <p className={`text-sm ${textSub}`}>
                {language === 'es'
                  ? 'Los KPIs operacionales se calcularán cuando se re-ejecute el análisis. Se usará la pestaña "TIMES CALCULATION" del dataset como fuente principal y los timestamps AODB como validación cruzada.'
                  : 'Operational KPIs will be calculated on next analysis run. The "TIMES CALCULATION" sheet will be the primary source, with AODB timestamps as cross-validation.'}
              </p>
            </div>
          </>
        ) : (
          /* AODB data available — show full KPIs */
          <>
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className={`font-bold text-lg ${textMain} flex items-center gap-2`}>
              <Database size={20} className="text-cyan-400" />
              {language === 'es' ? 'Benchmarking Operacional (AODB)' : 'Operational Benchmarking (AODB)'}
            </h3>
            <p className={`text-xs ${textSub} mt-1`}>
              {language === 'es'
                ? `Fuente: ${sourceLabel}. ${quality.total_rows || 0} filas procesadas, ${global.total_valid_samples || 0} muestras válidas.`
                : `Source: ${sourceLabel}. ${quality.total_rows || 0} rows processed, ${global.total_valid_samples || 0} valid samples.`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Data source badge */}
            {dataSource === 'both' && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1
                bg-cyan-500/20 text-cyan-400`}>
                <Shield size={10} />
                {language === 'es' ? 'Validación cruzada' : 'Cross-validated'}
              </span>
            )}
            {/* Data quality badge */}
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5
              ${(quality.tat_quality_pct || 0) > 70
                ? 'bg-emerald-500/20 text-emerald-400'
                : (quality.tat_quality_pct || 0) > 30
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'bg-red-500/20 text-red-400'
              }`}>
              <CheckCircle2 size={12} />
              {quality.tat_quality_pct || 0}% {language === 'es' ? 'calidad TAT' : 'TAT quality'}
            </div>
          </div>
        </div>

        {/* AIRPORT GLOBAL KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <KpiCard
            label={language === 'es' ? 'Taxi In (avg)' : 'Taxi In (avg)'}
            value={global.avg_taxi_in_min != null ? `${global.avg_taxi_in_min} min` : null}
            sub="AIBT − ALDT" color="blue" isDark={isDark}
            badge={global.primary_source === 'Times_Calculation' ? 'AODB' : undefined}
          />
          <KpiCard
            label="TAT (avg)"
            value={global.avg_tat_min != null ? `${global.avg_tat_min} min` : null}
            sub="AOBT − AIBT" color="orange" isDark={isDark}
            badge={global.primary_source === 'Times_Calculation' ? 'AODB' : undefined}
          />
          <KpiCard
            label={language === 'es' ? 'Taxi Out (avg)' : 'Taxi Out (avg)'}
            value={global.avg_taxi_out_min != null ? `${global.avg_taxi_out_min} min` : null}
            sub="ATOT − AOBT" color="blue" isDark={isDark}
            badge={global.primary_source === 'Times_Calculation' ? 'AODB' : undefined}
          />
          <KpiCard
            label="Ground Time"
            value={global.avg_ground_time_min != null ? `${global.avg_ground_time_min} min` : null}
            sub="ATOT − ALDT" color="purple" isDark={isDark}
          />
          <KpiCard
            label={language === 'es' ? 'TAT Programado' : 'Scheduled TAT'}
            value={global.avg_scheduled_tat_min != null ? `${global.avg_scheduled_tat_min} min` : null}
            sub={language === 'es' ? 'STD − STA' : 'STD − STA'} color="cyan" isDark={isDark}
            badge={global.avg_scheduled_tat_min != null ? 'AODB' : undefined}
          />
          <KpiCard
            label={language === 'es' ? 'Desv. Llegada' : 'Arr. Deviation'}
            value={global.avg_arr_deviation_min != null ? `${global.avg_arr_deviation_min > 0 ? '+' : ''}${global.avg_arr_deviation_min} min` : null}
            sub="ALDT − STA" color={global.avg_arr_deviation_min > 15 ? 'red' : 'green'} isDark={isDark}
          />
          <KpiCard
            label={language === 'es' ? 'Desv. Salida' : 'Dep. Deviation'}
            value={global.avg_dep_deviation_min != null ? `${global.avg_dep_deviation_min > 0 ? '+' : ''}${global.avg_dep_deviation_min} min` : null}
            sub="ATOT − STD" color={global.avg_dep_deviation_min > 15 ? 'red' : 'green'} isDark={isDark}
            badge={global.primary_source === 'Times_Calculation' ? 'AODB' : undefined}
          />
        </div>

        {/* CROSS-VALIDATION SECTION */}
        {Object.keys(crossValidation).length > 0 && (
          <div className="mb-4">
            <button
              onClick={() => setShowCrossValidation(!showCrossValidation)}
              className={`flex items-center gap-2 text-sm font-bold ${textMain} hover:text-cyan-400 transition-colors mb-2`}
            >
              {showCrossValidation ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <RefreshCw size={16} className="text-cyan-400" />
              {language === 'es'
                ? 'Validación Cruzada: Times_Calculation vs Timestamps'
                : 'Cross-Validation: Times_Calculation vs Timestamps'}
            </button>

            {showCrossValidation && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(crossValidation).map(([key, val]: [string, any]) => {
                    const labels: Record<string, string> = {
                      taxi_in: 'Taxi In', taxi_out: 'Taxi Out', tat: 'TAT', dep_delay: language === 'es' ? 'Desv. Salida' : 'Dep. Delay'
                    };
                    return (
                      <div key={key} className="text-center">
                        <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] mb-2">{labels[key] || key}</p>
                        <div className="flex items-center justify-center gap-3">
                          <div>
                            <p className="text-[9px] text-[var(--text-tertiary)]">Precalc</p>
                            <p className="text-sm font-bold text-blue-400">{val.precalc ?? '—'}</p>
                          </div>
                          <span className="text-[var(--text-tertiary)] text-xs">vs</span>
                          <div>
                            <p className="text-[9px] text-[var(--text-tertiary)]">{language === 'es' ? 'Calculado' : 'Calc.'}</p>
                            <p className="text-sm font-bold text-purple-400">{val.calculated ?? '—'}</p>
                          </div>
                        </div>
                        <div className="mt-1 flex items-center justify-center gap-1">
                          <DeltaIndicator value={val.delta} />
                          <span className={`text-[9px] ml-1 ${val.consistent ? 'text-emerald-400' : 'text-amber-400'}`}>
                            ({val.pct_diff}%)
                          </span>
                          {val.consistent
                            ? <CheckCircle2 size={10} className="text-emerald-400" />
                            : <AlertTriangle size={10} className="text-amber-400" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className={`text-[10px] mt-3 text-center ${textSub}`}>
                  {language === 'es'
                    ? '✅ = Diferencia < 15% (datos consistentes). ⚠️ = Diferencia > 15% (verificar datos).'
                    : '✅ = Diff < 15% (data consistent). ⚠️ = Diff > 15% (check data).'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* TAT Distribution */}
        {(global.p10_tat_min || global.p90_tat_min) && (
          <div className={`p-3 rounded-lg ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-50'} mb-4`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-2">
              {language === 'es' ? 'Distribución TAT (Time on Stand)' : 'TAT Distribution (Time on Stand)'}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-tertiary)]">P10</span>
                <span className="text-sm font-bold text-blue-400">{global.p10_tat_min} min</span>
              </div>
              <div className="flex-1 h-2 bg-gradient-to-r from-blue-500/30 via-orange-500/30 to-red-500/30 rounded-full relative">
                <div
                  className="absolute h-4 w-1 bg-orange-400 rounded -top-1"
                  style={{
                    left: global.p90_tat_min > 0
                      ? `${((global.p50_tat_min - global.p10_tat_min) / (global.p90_tat_min - global.p10_tat_min)) * 100}%`
                      : '50%'
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-tertiary)]">P50</span>
                <span className="text-sm font-bold text-orange-400">{global.p50_tat_min} min</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--text-tertiary)]">P90</span>
                <span className="text-sm font-bold text-red-400">{global.p90_tat_min} min</span>
              </div>
            </div>
          </div>
        )}

        {/* BY CARRIER TABLE */}
        {carriers.length > 0 && (
          <div className="mb-4">
            <button
              onClick={() => setShowCarrierTable(!showCarrierTable)}
              className={`flex items-center gap-2 text-sm font-bold ${textMain} hover:text-cyan-400 transition-colors mb-2`}
            >
              {showCarrierTable ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <BarChart3 size={16} className="text-cyan-400" />
              {language === 'es'
                ? `KPIs por Aerolínea (${carriers.length})`
                : `KPIs by Carrier (${carriers.length})`}
            </button>

            {showCarrierTable && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-[var(--border)]' : 'border-gray-200'}`}>
                      <th className={`px-3 py-2 text-left font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Aerolínea' : 'Carrier'}
                      </th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Vuelos' : 'Flights'}
                      </th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Taxi In</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>TAT</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Taxi Out</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Ground Time</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Desv. Arr' : 'Arr Dev'}
                      </th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Desv. Dep' : 'Dep Dev'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-[var(--border)]/50' : 'divide-gray-100'}`}>
                    {carriers.map((c: any, i: number) => (
                      <tr key={i} className={`${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
                        <td className={`px-3 py-2 font-bold ${textMain}`}>{c.carrier}</td>
                        <td className={`px-3 py-2 text-right ${textSub}`}>{c.flights}</td>
                        <td className={`px-3 py-2 text-right ${textMain}`}>
                          {c.avg_taxi_in != null ? `${c.avg_taxi_in}` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-bold text-orange-400`}>
                          {c.avg_tat != null ? `${c.avg_tat}` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right ${textMain}`}>
                          {c.avg_taxi_out != null ? `${c.avg_taxi_out}` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right text-purple-400`}>
                          {c.avg_ground_time != null ? `${c.avg_ground_time}` : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right`}>
                          {c.avg_arr_deviation != null
                            ? <span className={c.avg_arr_deviation > 15 ? 'text-red-400' : 'text-emerald-400'}>
                                {c.avg_arr_deviation > 0 ? '+' : ''}{c.avg_arr_deviation}
                              </span>
                            : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right`}>
                          {c.avg_dep_deviation != null
                            ? <span className={c.avg_dep_deviation > 15 ? 'text-red-400' : 'text-emerald-400'}>
                                {c.avg_dep_deviation > 0 ? '+' : ''}{c.avg_dep_deviation}
                              </span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* BY AIRCRAFT TYPE TABLE */}
        {types.length > 0 && (
          <div>
            <button
              onClick={() => setShowTypeTable(!showTypeTable)}
              className={`flex items-center gap-2 text-sm font-bold ${textMain} hover:text-cyan-400 transition-colors mb-2`}
            >
              {showTypeTable ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Plane size={16} className="text-cyan-400" />
              {language === 'es'
                ? `KPIs por Tipo de Aeronave (${types.length})`
                : `KPIs by Aircraft Type (${types.length})`}
            </button>

            {showTypeTable && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-[var(--border)]' : 'border-gray-200'}`}>
                      <th className={`px-3 py-2 text-left font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Tipo' : 'Type'}
                      </th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>
                        {language === 'es' ? 'Vuelos' : 'Flights'}
                      </th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Taxi In</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>TAT</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Taxi Out</th>
                      <th className={`px-3 py-2 text-right font-bold uppercase ${textSub}`}>Ground Time</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-[var(--border)]/50' : 'divide-gray-100'}`}>
                    {types.map((t: any, i: number) => (
                      <tr key={i} className={`${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
                        <td className={`px-3 py-2 font-bold font-mono ${textMain}`}>{t.arctyp}</td>
                        <td className={`px-3 py-2 text-right ${textSub}`}>{t.flights}</td>
                        <td className={`px-3 py-2 text-right ${textMain}`}>{t.avg_taxi_in ?? '—'}</td>
                        <td className={`px-3 py-2 text-right font-bold text-orange-400`}>{t.avg_tat ?? '—'}</td>
                        <td className={`px-3 py-2 text-right ${textMain}`}>{t.avg_taxi_out ?? '—'}</td>
                        <td className={`px-3 py-2 text-right text-purple-400`}>{t.avg_ground_time ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </div>

      {/* GOSCORE BENCHMARKING SECTION */}
      <div className={`p-6 rounded-xl border ${cardBg}`}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className={`font-bold text-lg ${textMain} flex items-center gap-2`}>
              <TrendingUp size={20} className="text-emerald-400" />
              {language === 'es' ? 'GOscore Benchmarking' : 'GOscore Benchmarking'}
            </h3>
            <p className={`text-xs ${textSub} mt-1`}>
              {language === 'es'
                ? 'Compara tus KPIs operacionales AODB con datos observados de GOscore (ADS-B satelital).'
                : 'Compare your AODB operational KPIs with GOscore observed data (satellite ADS-B).'}
            </p>
          </div>
          
          {/* Upload button */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all
                ${uploading
                  ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                  : 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30'
                }`}
            >
              <Upload size={16} />
              {uploading
                ? (language === 'es' ? 'Procesando...' : 'Processing...')
                : (language === 'es' ? 'Importar CSV GOscore' : 'Import GOscore CSV')}
            </button>
          </div>
        </div>

        {/* Upload Error */}
        {uploadError && (
          <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
            <AlertTriangle size={16} className="text-red-400 shrink-0" />
            <p className="text-xs text-red-400">{uploadError}</p>
          </div>
        )}

        {/* Upload Success */}
        {uploadResult && (
          <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 ${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
            <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
            <p className="text-xs text-emerald-400">
              {language === 'es'
                ? `${uploadResult.carriers_parsed} aerolíneas parseadas, ${uploadResult.carriers_matched} matched con AODB (${uploadResult.match_rate}%)`
                : `${uploadResult.carriers_parsed} carriers parsed, ${uploadResult.carriers_matched} matched with AODB (${uploadResult.match_rate}%)`}
            </p>
          </div>
        )}

        {/* DATE RANGE MODAL */}
        {showDateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`w-full max-w-md p-6 rounded-2xl shadow-2xl border ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-gray-200'}`}>
              <h3 className={`font-bold text-lg mb-1 ${textMain} flex items-center gap-2`}>
                <Calendar size={20} className="text-emerald-400" />
                {language === 'es' ? 'Período del CSV GOscore' : 'GOscore CSV Period'}
              </h3>
              <p className={`text-xs ${textSub} mb-5`}>
                {language === 'es'
                  ? 'Indica el rango de fechas que cubre el CSV de GOscore. Los datos AODB se filtrarán al mismo período para una comparación justa.'
                  : 'Enter the date range covered by the GOscore CSV. AODB data will be filtered to the same period for a fair comparison.'}
              </p>

              {pendingFile && (
                <div className={`p-3 rounded-lg mb-4 flex items-center gap-2 ${isDark ? 'bg-emerald-900/20' : 'bg-emerald-50'}`}>
                  <Upload size={14} className="text-emerald-400" />
                  <span className={`text-xs font-mono ${textSub}`}>{pendingFile.name}</span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className={`block text-xs font-bold mb-1.5 ${textSub}`}>
                    {language === 'es' ? 'Fecha inicio' : 'Start date'}
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={`w-full p-2.5 rounded-lg border text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none
                      ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)] text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
                <div>
                  <label className={`block text-xs font-bold mb-1.5 ${textSub}`}>
                    {language === 'es' ? 'Fecha fin' : 'End date'}
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={`w-full p-2.5 rounded-lg border text-sm font-medium focus:ring-2 focus:ring-emerald-500 outline-none
                      ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)] text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                  />
                </div>
              </div>

              {(!dateFrom || !dateTo) && (
                <p className={`text-xs ${textSub} mb-4 italic`}>
                  {language === 'es'
                    ? '⚠️ Si no introduces fechas, se compararán con TODOS los datos AODB (puede no ser representativo).'
                    : '⚠️ Without dates, comparison will use ALL AODB data (may not be representative).'}
                </p>
              )}

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => { setShowDateModal(false); setPendingFile(null); }}
                  className={`px-4 py-2 rounded-lg text-sm font-bold ${isDark ? 'text-gray-400 hover:bg-white/5' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleConfirmUpload}
                  className="px-5 py-2 rounded-lg text-sm font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors flex items-center gap-2"
                >
                  <Upload size={14} />
                  {language === 'es' ? 'Subir y comparar' : 'Upload & Compare'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BASELINE DEVIATION PANEL — shows when period_kpis are available */}
        {baselineDeviation && Object.keys(baselineDeviation).length > 0 && (
          <div className={`p-4 rounded-lg mb-4 ${isDark ? 'bg-cyan-900/10 border border-cyan-500/20' : 'bg-cyan-50 border border-cyan-200'}`}>
            <h4 className={`text-sm font-bold mb-3 ${textMain} flex items-center gap-2`}>
              <BarChart3 size={16} className="text-cyan-400" />
              {language === 'es'
                ? `Desviación del Período ${benchmark?.date_from || ''} → ${benchmark?.date_to || ''} vs Baseline Global`
                : `Period ${benchmark?.date_from || ''} → ${benchmark?.date_to || ''} vs Global Baseline`}
              {periodKpis?.period && (
                <span className={`text-[10px] font-normal px-2 py-0.5 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-700'}`}>
                  {periodKpis.period.filtered_rows} / {periodKpis.period.total_rows} {language === 'es' ? 'vuelos' : 'flights'}
                  {' '}({periodKpis.period.coverage_pct}%)
                </span>
              )}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(baselineDeviation).map(([key, dev]: [string, any]) => {
                const labels: Record<string, string> = { taxi_in: 'Taxi In', taxi_out: 'Taxi Out', tat: 'TAT (Turnaround)' };
                const isWithin = dev.within_1_sigma;
                return (
                  <div key={key} className={`p-3 rounded-lg border ${isDark ? 'bg-[var(--surface-raised)] border-[var(--border)]' : 'bg-white border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-xs font-bold uppercase ${textSub}`}>{labels[key] || key}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold
                        ${isWithin === true ? 'bg-emerald-500/20 text-emerald-400' :
                          isWithin === false ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {isWithin === true ? '±1σ ✓' : isWithin === false ? `>${Math.abs(dev.z_score || 0).toFixed(1)}σ` : 'N/A'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <span className={textSub}>{language === 'es' ? 'Período' : 'Period'}:</span>
                      <span className={`font-bold ${textMain}`}>{dev.period_avg ?? '—'} min</span>
                      <span className={textSub}>Baseline:</span>
                      <span className={`font-bold ${textMain}`}>{dev.precalc_avg ?? '—'} min</span>
                      <span className={textSub}>Δ:</span>
                      <span className={`font-bold ${(dev.delta || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {dev.delta != null ? `${dev.delta > 0 ? '+' : ''}${dev.delta} min` : '—'}
                        {dev.pct_diff != null && <span className="text-[10px] ml-1 opacity-70">({dev.pct_diff}%)</span>}
                      </span>
                      <span className={textSub}>σ:</span>
                      <span className={`font-mono ${textSub}`}>{dev.std_dev ?? '—'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* GOscore Data Display */}
        {goscoreData && goscoreData.carriers && goscoreData.carriers.length > 0 ? (
          <div className="space-y-4">
            {/* GOscore Airport Summary */}
            {goscoreData.airport_summary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <KpiCard
                  label="GOscore Taxi In" isDark={isDark} color="green"
                  value={goscoreData.airport_summary.avg_taxi_in != null ? `${goscoreData.airport_summary.avg_taxi_in} min` : null}
                  sub={`${goscoreData.airport_summary.total_flights || 0} flights`}
                  badge="GOscore"
                />
                <KpiCard
                  label="GOscore TAT" isDark={isDark} color="green"
                  value={goscoreData.airport_summary.avg_tat != null ? `${goscoreData.airport_summary.avg_tat} min` : null}
                  sub="ADS-B observed"
                  badge="GOscore"
                />
                <KpiCard
                  label="GOscore Ground Time" isDark={isDark} color="green"
                  value={goscoreData.airport_summary.avg_ground_time != null ? `${goscoreData.airport_summary.avg_ground_time} min` : null}
                  sub="Touchdown → Takeoff"
                  badge="GOscore"
                />
                <KpiCard
                  label={language === 'es' ? 'GOscore Desv. Llegada' : 'GOscore Arr. Deviation'} isDark={isDark} color="green"
                  value={goscoreData.airport_summary.avg_arrival_diff != null ? `${goscoreData.airport_summary.avg_arrival_diff > 0 ? '+' : ''}${goscoreData.airport_summary.avg_arrival_diff} min` : null}
                  sub="Schedule Deviation"
                  badge="GOscore"
                />
              </div>
            )}

            {/* Airport-level Comparison: AODB vs GOscore */}
            {comparison?.airport_comparison && Object.keys(comparison.airport_comparison).length > 0 && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-[var(--surface-raised)]' : 'bg-gray-50'} mb-4`}>
                <p className="text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)] mb-3">
                  {language === 'es' ? 'Comparativa de Aeropuerto: AODB vs GOscore' : 'Airport Comparison: AODB vs GOscore'}
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { key: 'taxi_in', label: 'Taxi In', aodb: comparison.airport_comparison.aodb_avg_taxi_in, gs: comparison.airport_comparison.goscore_avg_taxi_in, delta: comparison.airport_comparison.delta_taxi_in },
                    { key: 'tat', label: 'TAT', aodb: comparison.airport_comparison.aodb_avg_tat, gs: comparison.airport_comparison.goscore_avg_tat, delta: comparison.airport_comparison.delta_tat },
                    { key: 'taxi_out', label: 'Taxi Out', aodb: comparison.airport_comparison.aodb_avg_taxi_out, gs: comparison.airport_comparison.goscore_avg_taxi_out, delta: comparison.airport_comparison.delta_taxi_out },
                  ].map(item => (
                    <div key={item.key} className="text-center">
                      <p className="text-[10px] font-bold uppercase text-[var(--text-secondary)] mb-2">{item.label}</p>
                      <div className="flex items-center justify-center gap-4">
                        <div>
                          <p className="text-xs text-[var(--text-tertiary)]">AODB</p>
                          <p className="text-lg font-bold text-blue-400">{item.aodb != null ? `${item.aodb}` : '—'}</p>
                        </div>
                        <span className="text-[var(--text-tertiary)]">vs</span>
                        <div>
                          <p className="text-xs text-[var(--text-tertiary)]">GOscore</p>
                          <p className="text-lg font-bold text-emerald-400">{item.gs != null ? `${item.gs}` : '—'}</p>
                        </div>
                      </div>
                      <DeltaIndicator value={item.delta} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Carrier Comparison Table */}
            {comparison?.carrier_comparison && comparison.carrier_comparison.length > 0 && (
              <div>
                <button
                  onClick={() => setShowGoscoreComparison(!showGoscoreComparison)}
                  className={`flex items-center gap-2 text-sm font-bold ${textMain} hover:text-emerald-400 transition-colors mb-2`}
                >
                  {showGoscoreComparison ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  <TrendingUp size={16} className="text-emerald-400" />
                  {language === 'es'
                    ? `Comparativa por Aerolínea (${comparison.carrier_comparison.length} matched)`
                    : `Carrier Comparison (${comparison.carrier_comparison.length} matched)`}
                </button>

                {showGoscoreComparison && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={`border-b ${isDark ? 'border-[var(--border)]' : 'border-gray-200'}`}>
                          <th className={`px-2 py-2 text-left font-bold uppercase ${textSub}`}>
                            {language === 'es' ? 'Aerolínea' : 'Carrier'}
                          </th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>AODB Flts</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>GS Flts</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>AODB TI</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>GS TI</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>Δ</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`} title="AODB median TAT (robust vs overnight outliers)">AODB TAT <span className="text-[10px] font-normal opacity-60">p50</span></th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>GS TAT</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>Δ</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>AODB GT</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>GS GT</th>
                          <th className={`px-2 py-2 text-right font-bold uppercase ${textSub}`}>Δ</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? 'divide-[var(--border)]/50' : 'divide-gray-100'}`}>
                        {comparison.carrier_comparison.map((row: any, i: number) => (
                          <tr key={i} className={`${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'} transition-colors`}>
                            <td className={`px-2 py-2 font-bold ${textMain}`}>{row.carrier}</td>
                            <td className={`px-2 py-2 text-right text-blue-400`}>{row.aodb_flights ?? '—'}</td>
                            <td className={`px-2 py-2 text-right text-emerald-400`}>{row.goscore_flights ?? '—'}</td>
                            <td className={`px-2 py-2 text-right text-blue-400`}>{row.aodb_taxi_in ?? '—'}</td>
                            <td className={`px-2 py-2 text-right text-emerald-400`}>{row.goscore_taxi_in ?? '—'}</td>
                            <td className={`px-2 py-2 text-right`}><DeltaIndicator value={row.delta_taxi_in} /></td>
                            <td className={`px-2 py-2 text-right text-blue-400`} title={row.aodb_mean_tat != null ? `Mean: ${row.aodb_mean_tat} min` : undefined}>
                              {row.aodb_tat ?? '—'}
                              {row.aodb_mean_tat != null && <span className="ml-1 text-[10px] opacity-40">({row.aodb_mean_tat})</span>}
                            </td>
                            <td className={`px-2 py-2 text-right text-emerald-400`}>{row.goscore_tat ?? '—'}</td>
                            <td className={`px-2 py-2 text-right`}><DeltaIndicator value={row.delta_tat} /></td>
                            <td className={`px-2 py-2 text-right text-blue-400`}>{row.aodb_ground_time ?? '—'}</td>
                            <td className={`px-2 py-2 text-right text-emerald-400`}>{row.goscore_ground_time ?? '—'}</td>
                            <td className={`px-2 py-2 text-right`}><DeltaIndicator value={row.delta_ground_time} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Match stats */}
            {comparison?.match_stats && (
              <div className={`p-3 rounded-lg text-xs ${isDark ? 'bg-cyan-900/15' : 'bg-cyan-50'} flex items-center gap-2`}>
                <Database size={14} className="text-cyan-400" />
                <span className={textSub}>
                  {language === 'es'
                    ? `${comparison.match_stats.aodb_carriers} aerolíneas AODB, ${comparison.match_stats.goscore_carriers} GOscore, ${comparison.match_stats.matched} coincidentes (${comparison.match_stats.match_rate}%)`
                    : `${comparison.match_stats.aodb_carriers} AODB carriers, ${comparison.match_stats.goscore_carriers} GOscore, ${comparison.match_stats.matched} matched (${comparison.match_stats.match_rate}%)`}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-cyan-900/10' : 'bg-cyan-50'} text-center`}>
            <Upload size={32} className="text-cyan-400/50 mx-auto mb-2" />
            <p className={`text-sm ${textSub}`}>
              {language === 'es'
                ? 'Importa un CSV del GOscore TGT Tool para comparar con tus KPIs AODB.'
                : 'Import a GOscore TGT Tool CSV to compare with your AODB KPIs.'}
            </p>
            <p className={`text-xs ${isDark ? 'text-[var(--text-tertiary)]' : 'text-gray-400'} mt-1`}>
              {language === 'es'
                ? 'Descarga el CSV desde goscore.aero → TGT Tool → Download CSV'
                : 'Download CSV from goscore.aero → TGT Tool → Download CSV'}
            </p>
          </div>
        )}

        {/* CSV filename */}
        {benchmark?.csv_filename && (
          <p className={`text-[10px] mt-3 flex items-center gap-1 ${isDark ? 'text-[var(--text-tertiary)]' : 'text-gray-400'}`}>
            <FileText size={11} /> {benchmark.csv_filename}
          </p>
        )}
      </div>
    </div>
  );
};

export default AodbBenchmarkSection;
